import React, { useEffect, useState } from 'react';
import { Paper, Box, Typography, IconButton, Grid, TextField, FormControlLabel, Switch } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { Question } from '../../types';
import QuestionAdvanced from './QuestionAdvanced';

export interface QuestionCardProps {
  question: Question & any;
  qIndex: number;
  questionsLength: number;
  moveQuestion: (index: number, dir: 'up' | 'down') => void;
  duplicateQuestion: (index: number) => void;
  removeQuestion: (index: number) => void;
  handleQuestionChange: (index: number, field: keyof Question, value: any) => void;
  setQuestions: React.Dispatch<React.SetStateAction<Question[]>>;
  handleOptionChange: (questionIndex: number, optionIndex: number, value: string) => void;
  addOption: (questionIndex: number) => void;
  removeOption: (questionIndex: number, optionIndex: number) => void;
  readOnly?: boolean;
}

const QuestionCard: React.FC<QuestionCardProps> = React.memo((props) => {
  const { question, qIndex, questionsLength, moveQuestion, duplicateQuestion, removeQuestion, handleQuestionChange, setQuestions, handleOptionChange, addOption, removeOption, readOnly } = props;
  const [draftText, setDraftText] = useState<string>(question.questionText || '');

  useEffect(() => {
    setDraftText(question.questionText || '');
  }, [question]);

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 2, contain: 'paint', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Question {qIndex + 1}</Typography>
        <Box>
          <IconButton onClick={() => moveQuestion(qIndex, 'up')} disabled={readOnly || qIndex === 0} size="small">
            <ArrowUpIcon />
          </IconButton>
          <IconButton onClick={() => moveQuestion(qIndex, 'down')} disabled={readOnly || qIndex === questionsLength - 1} size="small">
            <ArrowDownIcon />
          </IconButton>
          <IconButton onClick={() => duplicateQuestion(qIndex)} size="small" title="Duplicate" disabled={readOnly}>
            <CopyIcon />
          </IconButton>
          <IconButton onClick={() => removeQuestion(qIndex)} disabled={readOnly || questionsLength === 1} color="error" size="small">
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Question Text"
            value={draftText}
            onChange={readOnly ? undefined : (e) => setDraftText(e.target.value)}
            onBlur={readOnly ? undefined : () => {
              if (draftText !== question.questionText) {
                handleQuestionChange(qIndex, 'questionText', draftText);
              }
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); } }}
            disabled={readOnly}
            required
          />
          {question.questionType !== 'display' && !readOnly && (
            <FormControlLabel
              control={
                <Switch
                  checked={question.required}
                  onChange={(e) => handleQuestionChange(qIndex, 'required', e.target.checked)}
                />
              }
              label="Required"
            />
          )}
        </Grid>

        <QuestionAdvanced
          question={question as any}
          qIndex={qIndex}
          setQuestions={setQuestions}
          handleQuestionChange={handleQuestionChange}
          handleOptionChange={handleOptionChange}
          addOption={addOption}
          removeOption={removeOption}
          readOnly={readOnly}
        />
      </Grid>
    </Paper>
  );
}, (prev, next) => prev.question === next.question && prev.qIndex === next.qIndex);

export default QuestionCard;
